import {
  isEmpty,
  type MobileCredential,
  type UserSessionListInput,
  type UserSessionListSuccess,
  type UserSessionRevokeInput,
  type UserSessionRevokeSuccess,
  type UserId,
} from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import { useTantalimQuery } from '../query'
import { useTantalimMutation } from '../mutation'
import { userSessionListKey } from '../keys'
import { type GetHookResponse, type RemoveHookResponse, mutationRemove } from '../hooks'

export type LoginSession = UserSessionListSuccess['sessions'][number]
export type { MobileCredential }

export type UserSessions = {
  sessions: LoginSession[]
  mobileCredentials: MobileCredential[]
}

export function useUserSessions(enabled: boolean, userId: UserId): GetHookResponse<UserSessions> {
  return useTantalimQuery<UserSessionListInput, UserSessionListSuccess, UserSessions>({
    url: '/user/session/list',
    queryKey: userSessionListKey(userId),
    input: {},
    convertResponse: ({ sessions, mobileCredentials }) => ({ sessions, mobileCredentials }),
    options: { enabled },
  })
}

export function useRevokeUserSessions(userId: UserId): RemoveHookResponse<UserSessionRevokeInput> {
  const queryClient = useQueryClient()
  const mutation = useTantalimMutation<UserSessionRevokeInput, UserSessionRevokeSuccess>({
    url: '/user/session/revoke',
  })

  async function remove(input: UserSessionRevokeInput): Promise<void> {
    await mutation.mutateAsync(input)
    queryClient.setQueryData<UserSessions>(userSessionListKey(userId), (data) => {
      if (!data) return data
      if (isEmpty(input.sessionIds) && isEmpty(input.mobileCredentialIds)) {
        return { sessions: [], mobileCredentials: [] }
      }
      return {
        sessions: data.sessions.filter(({ sessionId }) => keepsListedId(input.sessionIds, sessionId)),
        mobileCredentials: data.mobileCredentials.filter(({ id }) => keepsListedId(input.mobileCredentialIds, id)),
      }
    })
  }

  return mutationRemove(mutation, remove)
}

function keepsListedId(selectedIds: string[] | undefined, id: string): boolean {
  if (isEmpty(selectedIds)) return true
  return !selectedIds.includes(id)
}
